import { FC, useState, useEffect, useContext } from 'react';
import { Box, FormHelperText, Grid, MenuItem } from '@mui/material';
import { UserContext } from '../../../context';
import { FiltersButtons, FiltersLayout, SMBSelect, SearchField } from '../../../components';
import {
  getDocumentationBuildStatuses,
  getDocumentationNeighborhoods,
  getDocumentationRoles
} from '../../../fetch';
import { IDropdownResponse } from '../../../models';

interface IBuildDocumentationFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setHasAppliedFilters: (val: boolean) => void;
  hasAppliedFilters?: boolean;
  selectedStatus: string;
  selectedRole: string;
  selectedNeighborhoodId: string;
  searchText: string;
  selectedClosingDateLocked: string;
  selectedTermiteLetterUploaded: string;
  selectedCOUploaded: string;
  setSelectedNeighborhoodId: (val: any) => void;
  setSelectedStatus: (val: any) => void;
  setSelectedRole: (val: any) => void;
  setSearchText: (val: any) => void;
  setSelectedClosingDateLocked: (val: any) => void;
  setSelectedTermiteLetterUploaded: (val: any) => void;
  setSelectedCOUploaded: (val: any) => void;
  fetchBuilds: () => void;
}

export const BuildDocumentationFilters: FC<IBuildDocumentationFilters> = ({
  isLoading,
  applyFilters,
  setHasAppliedFilters,
  hasAppliedFilters,
  selectedNeighborhoodId,
  setSelectedNeighborhoodId,
  selectedStatus,
  setSelectedStatus,
  selectedRole,
  setSelectedRole,
  searchText,
  setSearchText,
  selectedClosingDateLocked,
  setSelectedClosingDateLocked,
  selectedTermiteLetterUploaded,
  setSelectedTermiteLetterUploaded,
  selectedCOUploaded,
  setSelectedCOUploaded,
  fetchBuilds
}) => {
  const { isAdmin } = useContext(UserContext);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [statuses, setStatuses] = useState<IDropdownResponse[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [roles, setRoles] = useState<IDropdownResponse[]>([]);
  const [isLoadingNeighborhoods, setIsLoadingNeighborhoods] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState<IDropdownResponse[]>([]);

  const fetchStatuses = async () => {
    setIsLoadingStatuses(true);
    try {
      const res = await getDocumentationBuildStatuses();
      setStatuses(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  const fetchRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const res = await getDocumentationRoles();
      setRoles(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchBuildNeighborhoods = async () => {
    setIsLoadingNeighborhoods(true);
    try {
      const res = await getDocumentationNeighborhoods();
      setNeighborhoods(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingNeighborhoods(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
    fetchBuildNeighborhoods();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FiltersLayout alignItems="flex-start">
      <Grid item xs={12} md={4} xl={3}>
        <SMBSelect
          label={isLoadingStatuses ? 'Loading...' : 'Status'}
          disabled={isLoading || isLoadingStatuses}
          value={selectedStatus}
          onChange={e => {
            setSelectedStatus(e.target.value);
          }}
          handleClear={() => setSelectedStatus('')}
        >
          {statuses &&
            !isLoadingStatuses &&
            statuses.map((status, index) => {
              return (
                <MenuItem key={`${status.value}-${index}`} value={status.value}>
                  {status.description}
                </MenuItem>
              );
            })}
        </SMBSelect>
      </Grid>
      <Grid item xs={12} md={4} xl={3}>
        <SearchField
          placeholder="Search..."
          searchValue={searchText}
          setSearchValue={setSearchText}
          handleSearch={value => {
            setHasAppliedFilters(true);
            applyFilters();
          }}
          isDisabled={isLoading}
        />
      </Grid>
      <Grid item xs={12} md={4} xl={3}>
        <SMBSelect
          label={isLoadingNeighborhoods ? 'Loading...' : 'Neighborhood'}
          disabled={isLoading || isLoadingNeighborhoods}
          value={selectedNeighborhoodId}
          onChange={e => {
            setSelectedNeighborhoodId(e.target.value);
          }}
          handleClear={() => setSelectedNeighborhoodId('')}
        >
          {neighborhoods &&
            !isLoadingNeighborhoods &&
            neighborhoods.map((neighborhood, index) => {
              return (
                <MenuItem key={`${neighborhood.value}-${index}`} value={neighborhood.value}>
                  {neighborhood.description}
                </MenuItem>
              );
            })}
        </SMBSelect>
      </Grid>
      {isAdmin && (
        <Grid item xs={12} md={4} xl={3}>
          <SMBSelect
            label={isLoadingRoles ? 'Loading...' : 'Documentation Group'}
            disabled={isLoading || isLoadingRoles}
            value={selectedRole}
            required={isAdmin && selectedStatus === 'BuildsRequiringAction' && selectedRole === ''}
            error={
              isAdmin && selectedStatus === 'BuildsRequiringAction' && selectedRole === ''
                ? true
                : false
            }
            onChange={e => {
              setSelectedRole(e.target.value);
            }}
            handleClear={() => setSelectedRole('')}
          >
            {roles &&
              !isLoadingRoles &&
              roles.map((role, index) => {
                return (
                  <MenuItem key={`${role.value}-${index}`} value={role.value}>
                    {role.description}
                  </MenuItem>
                );
              })}
          </SMBSelect>
          {isAdmin && selectedStatus === 'BuildsRequiringAction' && selectedRole === '' && (
            <FormHelperText sx={{ color: theme => theme.palette.error.main }}>
              Required
            </FormHelperText>
          )}
        </Grid>
      )}
      <Grid item xs={12} md={4} xl={3}>
        <SMBSelect
          label="Closing Date Locked"
          disabled={isLoading}
          value={selectedClosingDateLocked}
          onChange={e => {
            setSelectedClosingDateLocked(e.target.value);
          }}
          handleClear={() => setSelectedClosingDateLocked('')}
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </SMBSelect>
      </Grid>
      <Grid item xs={12} md={4} xl={3}>
        <SMBSelect
          label="Termite Letter Uploaded"
          disabled={isLoading}
          value={selectedTermiteLetterUploaded}
          onChange={e => {
            setSelectedTermiteLetterUploaded(e.target.value);
          }}
          handleClear={() => setSelectedTermiteLetterUploaded('')}
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </SMBSelect>
      </Grid>
      <Grid item xs={12} md={4} xl={3}>
        <SMBSelect
          label="Certificate of Occupancy Uploaded"
          disabled={isLoading}
          value={selectedCOUploaded}
          onChange={e => {
            setSelectedCOUploaded(e.target.value);
          }}
          handleClear={() => setSelectedCOUploaded('')}
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </SMBSelect>
      </Grid>
      <Grid item xs={12} sm={6} xl={3}>
        <Box
          sx={{
            '& > button': {
              marginTop: 0
            }
          }}
        >
          <FiltersButtons
            hasAppliedFilters={hasAppliedFilters}
            isDisabled={
              isLoading ||
              !selectedStatus ||
              (isAdmin && selectedStatus === 'BuildsRequiringAction' && selectedRole === '')
            }
            handleApplyFilters={() => {
              setHasAppliedFilters(true);
              applyFilters();
            }}
            handleResetFilters={() => {
              setHasAppliedFilters(false);
              setSelectedNeighborhoodId('');
              setSelectedStatus('');
              setSelectedRole('');
              setSearchText('');
              setSelectedClosingDateLocked('');
              setSelectedTermiteLetterUploaded('');
              setSelectedCOUploaded('');
              applyFilters(true);
            }}
          />
        </Box>
      </Grid>
    </FiltersLayout>
  );
};
